import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useApi } from './ApiContext';

export type NotificationSeverity = 'info' | 'warning' | 'error' | 'success';
export type NotificationType = 'stock_low' | 'order_due' | 'order_completed' | 'system' | 'alert';

export interface Notification {
  id: string;
  type: NotificationType;
  severity: NotificationSeverity;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
  actionLabel?: string;
  metadata?: Record<string, any>;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { api } = useApi();
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Cargar notificaciones desde localStorage al iniciar
  useEffect(() => {
    const saved = localStorage.getItem('notifications');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setNotifications(
          parsed.map((n: any) => ({
            ...n,
            timestamp: new Date(n.timestamp),
          }))
        );
      } catch (e) {
        console.error('Error loading notifications:', e);
      }
    }
  }, []);

  // Guardar notificaciones en localStorage cuando cambian
  useEffect(() => {
    localStorage.setItem('notifications', JSON.stringify(notifications));
  }, [notifications]);

  // Cargar alertas del sistema desde el backend
  const refreshNotifications = useCallback(async () => {
    try {
      const response = await api.get('/notifications/alerts').catch((error) => {
        // Si falla, retornar un array vacío en lugar de lanzar el error
        console.warn('No se pudieron cargar notificaciones del servidor:', error);
        return { data: { alerts: [] } };
      });
      const alerts = response.data?.alerts || [];

      // Convertir alertas del backend a notificaciones
      const newNotifications: Notification[] = alerts.map((alert: any) => ({
        id: alert.id ? `alert-${alert.id}` : `alert-${Math.random().toString(36)}`,
        type: alert.type || 'alert',
        severity: alert.severity || 'warning',
        title: alert.title || 'Alerta',
        message: alert.message || '',
        timestamp: new Date(alert.timestamp || Date.now()),
        read: false,
        actionUrl: alert.actionUrl,
        actionLabel: alert.actionLabel,
        metadata: alert.metadata,
      }));

      // Actualizar estado de notificaciones
      setNotifications((prev) => {
        const incomingIds = new Set(newNotifications.map(n => n.id));
        
        // 1. Filtrar: Eliminar notificaciones de sistema antiguas que ya no vienen del backend
        // (Identificamos las de sistema por sus prefijos conocidos o si están en el set de incomingIds)
        // Si usamos IDs constantes como 'alert-stock-low-warning', podemos detectar si falta.
        const keptNotifications = prev.filter(p => {
            const isSystemAlert = p.id.startsWith('alert-stock-') || p.id.startsWith('alert-orders-');
            if (isSystemAlert && !incomingIds.has(p.id)) {
                // Es una alerta de sistema que ya no está activa -> Eliminar
                return false; 
            }
            return true; // Mantener manuales o alertas activas
        });

        // 2. Actualizar: Reemplazar las existentes con datos frescos (manteniendo estado 'read')
        const updatedNotifications = keptNotifications.map(p => {
            const fresh = newNotifications.find(n => n.id === p.id);
            if (fresh) {
                // Actualizar contenido pero mantener si estaba leída
                return { ...fresh, read: p.read };
            }
            return p;
        });

        // 3. Agregar: Insertar las nuevas que no existían
        const currentIds = new Set(updatedNotifications.map(n => n.id));
        const toAdd = newNotifications.filter(n => !currentIds.has(n.id));

        return [...toAdd, ...updatedNotifications]; // Poner nuevas al principio
      });
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error refreshing notifications:', error);
      }
    }
  }, [api]);

  // Refrescar notificaciones cada 5 minutos
  useEffect(() => {
    // Esperar un poco antes de hacer la primera llamada para evitar errores en la inicialización
    const timeout = setTimeout(() => {
      refreshNotifications().catch((error) => {
        if (import.meta.env.DEV) {
          console.warn('Error al cargar notificaciones iniciales (no crítico):', error);
        }
      });
    }, 1000);
    
    const interval = setInterval(() => {
      refreshNotifications().catch((error) => {
        if (import.meta.env.DEV) {
          console.warn('Error al refrescar notificaciones (no crítico):', error);
        }
      });
    }, 5 * 60 * 1000); // 5 minutos
    
    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [refreshNotifications]);

  const addNotification = useCallback(
    (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
      const newNotification: Notification = {
        ...notification,
        id: Math.random().toString(36).substring(2, 9),
        timestamp: new Date(),
        read: false,
      };
      setNotifications((prev) => [newNotification, ...prev]);
    },
    []
  );

  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        markAllAsRead,
        removeNotification,
        clearAll,
        refreshNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

